<template>
  <div class="content ok" v-on:click="sectionClicked">
    <perfect-scrollbar class="configMenu">
      <li :class="{active: pSelected && pSelected.id === item.id,
        mainPage: item.mainPage === 1}"
        v-for="(item, i) in pages" :key="`mp${i}`"
        v-on:click="selectPage(item)">
        <span v-if="item.name[lang]">{{item.name[lang]}}</span>
        <span v-else class="noName">Unnamed page</span>
        <p v-if="item.descriptions && item.descriptions[lang]">
          {{item.descriptions[lang]}}</p>
        <p v-else-if="item.name[lang]">Content for page {{item.name[lang].toLowerCase()}}</p>
        <p v-else>Content for this page</p>
        <div class="actionCnt">
          <div class="draft" v-if="item.status === 'draft'
            || item.hasDraft">Draft</div>
          <div class="actionItem more" v-on:click.stop="pageMore = item.id"
            :class="{open: pageMore === item.id}">
            <div class="actionMenu" v-if="pageMore === item.id"
              v-on:click.stop="doNothing">
              <li v-on:click="setAsMainPage(item)">
                <div class="icon main"></div> Set As Main Page</li>
              <template v-if="item.status === 'draft'">
                <div class="divider"></div>
                <li class="red" v-on:click="deleteDraft(item)">
                  <div class="icon delete"></div> Delete Draft</li>
              </template>
              <div class="divider"></div>
              <li class="red" v-on:click="deletePage(i, item)">
                <div class="icon delete"></div> Delete Page</li>
            </div>
          </div>
        </div>
      </li>
      <li class="loading" v-if="loadingPage"></li>
      <div class="addNewPage btnAdd" v-else
        v-on:click="addPage">Add web page</div>
    </perfect-scrollbar>
    <div class="ccTitle" v-if="pSelected">
      <div class="backBtn" v-on:click="pSelected = null"></div>
      {{pSelected.name[lang]}}
    </div>
    <div class="configContent hasEndSection" v-if="pSelected">
      <div class="sectionItem">
        <h2>Page Information</h2>
        <div class="configItem canEdit" v-on:click="editText('name', pSelected.name,
          'Page Name', pSelected, true)">
          <h4>Page Name</h4>
          <p v-if="pSelected.name && pSelected.name[lang]">{{pSelected.name[lang]}}</p>
        </div>
        <div class="configItem canEdit" v-on:click="editText('descriptions', pSelected.descriptions,
          'Page Descriptions', pSelected, true)">
          <h4>Page Descriptions</h4>
          <p><template v-if="pSelected.descriptions
            && pSelected.descriptions[lang]">{{pSelected.descriptions[lang]}}</template></p>
        </div>
      </div>
      <SectionData :sectionData="pSelected.sectionData" v-on:change="sectionChanged"
        v-on:click="sectionClicked"/>
    </div>
    <div class="endSection" v-if="pSelected">
      <div class="addSection btnAdd" v-on:click="addSection">Add section</div>
      <div class="actionCnt">
        <div class="actionItem history" v-on:click.stop="openHistory"
          :class="{open: historyOpen}">
          <span class="info">Revision history</span>
          <div class="actionMenu" v-if="historyOpen">
            <h4>Revision History</h4>
            <div class="historyCnt loading" v-if="loadingHistory"></div>
            <div class="historyCnt noData" v-else-if="history.length < 1">
              <div class="noDataIn">
                <img src="../assets/no-history.png">
                There are currently no history
              </div>
            </div>
            <div class="historyCnt" v-else v-on:click.stop="doNothing">
              <div class="historyItem" v-for="item in history" :key="`h${item.id}`"
                v-on:click.stop="selectHistory(item)">
                <h5>{{item.dateCreated | moment('from', 'now')}}</h5>
                <p v-if="item.author">edited by <b>{{item.author.name}}</b></p>
                <div class="historyStatus" :class="item.status">
                  {{item.status}}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="actionItem draft" :class="{loading: savingDraft, open: draftOpen}"
          v-on:click.stop="draftOpen = !draftOpen; historyOpen = false">
          <span class="info" v-if="savingDraft">Saving to draft...</span>
          <span class="info" v-else>Saved as draft</span>
          <div class="actionMenu" v-if="draftOpen" v-on:click.stop="doNothing">
            <li v-on:click="saveDraft">
              <div class="icon save"></div> Save as draft
            </li>
            <div class="divider"></div>
            <li class="red" v-on:click.stop="deleteDraft(pSelected)">
              <div class="icon delete"></div> Delete draft
            </li>
          </div>
        </div>
        <button class="btn btnSecondary" v-on:click="openPreview">Preview</button>
        <button class="btn btnPrimary" v-on:click="savePage('published')">Publish</button>
      </div>
    </div>
    <MediaSelector ref="mediaSelector"/>
    <MediaInputer ref="mediaInputer"/>
    <Snackbar ref="snackbar"/>
    <NumberEditor ref="numberEditor"/>
    <Confirmation ref="confirmDialog"/>
    <EditorDialog ref="editorDialog"/>
    <AddSocmed ref="addSocmed"/>
    <SectionSelector ref="sectionSelector"/>
    <LoadingDialog ref="loadingDialog"/>
    <AddIconSlider ref="addIconSlider"/>
    <ArticleSelector ref="articleSelector"/>
    <AddForm ref="addForm"/>
    <ColorPicker ref="colorPicker"/>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { nanoid } from 'nanoid';
import MediaSelector from '@/components/MediaSelector.vue';
import MediaInputer from '@/components/MediaInputer.vue';
import Snackbar from '@/components/Snackbar.vue';
import NumberEditor from '@/components/NumberEditor.vue';
import Confirmation from '@/components/Confirmation.vue';
import EditorDialog from '@/components/EditorDialog.vue';
import AddSocmed from '@/components/AddSocmed.vue';
import SectionSelector from '@/components/SectionSelector.vue';
import LoadingDialog from '@/components/LoadingDialog.vue';
import ArticleSelector from '@/components/ArticleSelector.vue';
import AddIconSlider from '@/components/AddIconSlider.vue';
import AddForm from '@/components/AddForm.vue';
import ColorPicker from '@/components/ColorPicker.vue';
import SectionData from '@/components/SectionData.vue';

export default {
  name: 'webConfig',
  components: {
    MediaSelector,
    MediaInputer,
    Snackbar,
    NumberEditor,
    Confirmation,
    EditorDialog,
    AddSocmed,
    SectionSelector,
    LoadingDialog,
    ArticleSelector,
    AddIconSlider,
    AddForm,
    ColorPicker,
    SectionData,
  },
  data() {
    return {
      loading: true,
      cf: null,
      menuActive: 'general',

      /**
       * Pages
       *
       */
      pageMore: null,
      loadingPage: true,
      savingDraft: false,
      pSelected: null,
      pages: [],

      /**
       * Sections
       *
       */
      sectionOptions: null,

      /**
       * Page draft
       *
       */
      draftOpen: false,

      /**
       * Page history
       *
       */
      historyOpen: false,
      loadingHistory: true,
      history: [],
    };
  },
  computed: {
    ...mapState(['lang']),
  },
  methods: {
    ...mapActions(['chTitle', 'chCGB', 'chDirId']),
    doNothing() {},
    sectionClicked() {
      this.draftOpen = false;
      this.historyOpen = false;
      this.pageMore = null;
      this.sectionOptions = null;
    },
    selectPage(item) {
      window.history.replaceState({}, item.id, `${this.$config.appPath}pages?page=${item.id}`);
      this.menuActive = null;
      this.pSelected = null;
      setTimeout(() => {
        this.pSelected = item;
      }, 50);
    },
    editText(fieldName, fieldValue, title, type, multiLang) {
      const options = [];
      const fieldType = (title.indexOf('Desc') > 0 || title.indexOf('address') >= 0
        || title.indexOf('Note') >= 0 || title.indexOf('Info') >= 0) ? 'textarea' : 'text';
      this.$refs.mediaInputer.open({
        title,
        options,
        placeholder: title,
        type: fieldType,
        model: fieldValue,
        action: 'Save Changes',
        multiLang,
      }, (b, v) => {
        if (!b) return;
        type[fieldName] = v; // eslint-disable-line
        this.savePage('draft');
      });
    },

    /**
     * Page
     *
     */
    getPage(next) {
      this.$http.get(this.$config.uri('page/all?detail=y')).then((r) => {
        this.loadingPage = false;
        this.pages = (r.data.status === 200) ? r.data.data : [];
        if (next) next();
      });
    },
    setAsMainPage(item) {
      this.pageMore = null;
      const name = item.name[this.lang] || 'Unnamed page';
      this.$refs.confirmDialog.open({
        title: 'Set As Main Page',
        desc: `Are you sure you want to make ${name} as a main page?`,
        action: 'Save',
      }, (b) => {
        if (!b) return;
        // eslint-disable-next-line
        for (const page of this.pages) {
          page.mainPage = 0;
        }
        item.mainPage = 1; // eslint-disable-line
        this.$http.get(this.$config.uri(`page/${item.id}/main`)).then((r) => {
          this.$refs.snackbar.open({
            content: `${name} has been set as main page`,
          });
        });
      });
    },
    saveDraft() {
      this.draftOpen = false;
      this.savePage('draft', () => {
        this.$refs.snackbar.open({ content: 'Page saved to draft' });
      });
    },
    sectionChanged(sectionData) {
      this.pSelected.sectionData = sectionData;
      this.savePage('draft');
    },
    deletePage(i, item) {
      this.pageMore = null;
      this.$refs.confirmDialog.open({
        title: `Delete Page ${item.name[this.lang]}`,
        desc: 'Are you sure you want to delete this page? This operation can not be undone',
      }, (b) => {
        if (!b) return;
        if (this.pSelected && this.pSelected.id === item.id) this.pSelected = null;
        this.pages.splice(i, 1);
        this.$http.get(this.$config.uri(`page/${item.id}/delete`)).then((r) => {
          this.$refs.snackbar.open({ content: 'Page has been deleted' });
        });
      });
    },
    savePage(status, next) {
      if (status === 'draft') {
        if (this.savingDraft) return;
        this.savingDraft = true;
      } else {
        this.$refs.loadingDialog.open();
      }
      const formData = JSON.parse(JSON.stringify(this.pSelected));
      formData.status = status;
      this.$http.post(this.$config.uri('page/save'), formData).then((r) => {
        this.savingDraft = false;
        if (status === 'draft') {
          this.pSelected.status = 'draft';
          this.pSelected.hasDraft = true;
        } else {
          this.$refs.snackbar.open({ content: 'Page has been published' });
          this.$refs.loadingDialog.close();
          this.pSelected.status = 'published';
          this.pSelected.hasDraft = false;
        }
        if (next) next(r.data);
      });
    },
    addPage() {
      this.$refs.confirmDialog.open({
        title: 'Add Web Page',
        desc: 'Please input name for the new page below',
        inputs: [
          {
            label: 'Name',
            placeholder: 'Page name...',
            type: 'text',
            value: { id: '', en: '' },
          },
          {
            label: 'Descriptions',
            placeholder: 'Page descriptions...',
            type: 'longtext',
            value: { id: '', en: '' },
          },
        ],
        action: 'Add Page',
        multiLang: true,
      }, (b, c, d, v) => {
        if (!b) return;
        this.$refs.loadingDialog.open();
        const page = {
          id: 0,
          name: v[0].value,
          descriptions: v[1].value,
          sectionData: [],
        };
        this.pages.push(page);
        this.selectPage(page);
        this.pSelected = page;

        /**
         * Save page as draft
         *
         */
        this.savePage('draft', (r) => {
          this.$refs.loadingDialog.close();
          page.id = r.id;
        });
      });
    },

    /**
     * Draft
     *
     */
    deleteDraft(item) {
      this.pageMore = null;
      this.draftOpen = false;
      this.$refs.confirmDialog.open({
        title: `Delete Draft ${item.name[this.lang]}`,
        desc: 'Are you sure you want to delete draft for this page? All changes will be lost',
      }, (b) => {
        if (!b) return;
        this.$refs.loadingDialog.open();
        this.$http.get(this.$config.uri(`page/${item.id}/draft/delete`)).then((r) => {
          this.getPage(() => {
            this.$refs.loadingDialog.close();
            if (item.status === 'draft') {
              if (this.pages.length > 0) this.selectPage(this.pages[0]);
            } else {
              // eslint-disable-next-line
              for (const p of this.pages) {
                if (item.id === p.id) {
                  this.selectPage(p);
                }
              }
            }
          });
        });
      });
    },

    /**
     * History
     *
     */
    openHistory() {
      this.draftOpen = false;
      this.historyOpen = !this.historyOpen;
      if (!this.historyOpen) return;
      this.$http.get(this.$config.uri(`page/${this.pSelected.id}/history`)).then((r) => {
        this.loadingHistory = false;
        this.history = r.data.status === 200 ? r.data.data : [];
      });
    },
    selectHistory(item) {
      this.$refs.confirmDialog.open({
        title: 'Revert Changes',
        desc: 'Are you sure you want to revert state to this selected history item? Current changes will be lost',
        action: 'Revert Changes',
      }, (b) => {
        if (!b) return;
        this.$refs.loadingDialog.open();
        this.$http.get(this.$config.uri(`page/history/${item.id}/revert`)).then((r) => {
          this.$refs.loadingDialog.close();
          this.getPage(() => {
            this.$refs.loadingDialog.close();
            if (item.status === 'draft') {
              if (this.pages.length > 0) this.selectPage(this.pages[0]);
            } else {
              // eslint-disable-next-line
              for (const p of this.pages) {
                if (item.id === p.id) {
                  this.selectPage(p);
                }
              }
            }
          });
        });
      });
    },

    /**
     * Section
     *
     */
    addSection() {
      this.$refs.sectionSelector.open((b, o) => {
        if (!b) return;
        const section = {
          id: nanoid(),
          title: '',
          descriptions: '',
          color: '',
          type: o,
          items: [],
          config: {},
          collapsed: false,
          status: 'published',
        };
        if (o === 'Gallery') {
          section.config.galleryDisplayType = 'Animated';

          /**
           * Gallery are fixed 12 item
           * for display type Animated
           *
           */
          for (let i = 0; i < 12; i += 1) {
            section.items.push({});
          }
        } else if (o === 'Block Articles') {
          section.config.itemPerPage = 4;
          section.config.articleCount = 8;
          section.config.displayViewAll = false;
          section.config.sources = 'Auto Selections';
          section.config.displayType = 'Slider';
          section.config.viewAllLabel = { en: 'View All', id: 'Lihat Semua' };
          section.config.viewAllUrl = { en: '', id: '' };
          section.config.directory = null;
        } else if (o === 'Map') {
          section.config.mapboxStyle = 'mapbox://styles/mapbox/streets-v12';
          section.config.lat = -2.997868;
          section.config.lng = 118.683471;
          section.config.zoom = 7;
          section.config.showControl = true;
          section.config.interactive = true;
          section.config.wideScreen = true;
          section.config.fullScreen = true;
          section.config.attribution = false;
          section.config.attributionLabel = { id: '', en: '' };
          section.config.geojson = null;
        } else if (o === 'Custom Block') {
          section.config.code = null;
        } else if (o === 'Form') {
          section.config.thankYouTitle = { id: '', en: '' };
          section.config.thankYouDesc = { id: '', en: '' };
          section.config.emailVerification = true;
          section.config.displayAddress = false;
          section.config.displayNewsletter = false;
          section.config.displaySocmed = false;
        } else if (o === 'Subscription') {
          section.config.thankYouTitle = { id: '', en: '' };
          section.config.thankYouDesc = { id: '', en: '' };
          section.config.emailVerification = true;
        } else if (o === 'Slider') {
          section.config.fullScreen = true;
        } else if (o === 'Image Header') {
          section.config.imgHeaderDisplayType = 'croped';
        }
        this.pSelected.sectionData.push(section);
        this.savePage('draft');
      });
    },
    deleteItem(item, i) {
      this.$refs.confirmDialog.open({
        title: 'Delete Section Item',
        desc: `Are you sure you want to delete this section item?
          This operation can not be undone.`,
      }, (b) => {
        item.items.splice(i, 1);
        this.savePage('draft');
      });
    },
    openPreview() {
      this.$refs.loadingDialog.open();
      this.$http.get(this.$config.uri(`page/${this.pSelected.id}/detail`)).then((r) => {
        this.$refs.loadingDialog.close();
        if (r.data.status !== 200) return;
        let langCode = 'id-id';
        if (this.lang === 'en') langCode = 'en-us';
        const { page } = r.data.data;
        window.open(`${this.$config.frontEnd}${langCode}/page/preview/${page.code}/${page.sign || ''}`, '_blank').focus();
      });
    },
  },
  beforeDestroy() {},
  mounted() {
    this.chCGB(false);
    this.chDirId(0);
    this.chTitle('Page Editor');
    this.menuActive = (this.$route.query && this.$route.query.menu)
      ? this.$route.query.menu : 'general';
    this.getPage(() => {
      const { page } = this.$route.query;
      if (page) {
        const pageId = parseInt(page, 10);
        for (const item of this.pages) { // eslint-disable-line
          if (pageId === item.id) {
            this.selectPage(item);
            break;
          }
        }
      } else if (this.pages.length > 0) {
        // this.selectPage(this.pages[0]);
      }
    });
  },
};
</script>

<style scoped lang="scss">
.content.loading{
  background-image: url(../assets/loading-2.svg);
  background-size: 46px auto;
  background-position: center;
  background-repeat: no-repeat;
}
.content.ok{
  display: flex;
  padding: 0 !important;
  position: relative;
  @media (max-width: 1060px) {
    display: block;
  }
  .configMenu{
    height: calc(100vh - 49px);
    width: 300px;
    border-right: #eee 1px solid;
    box-sizing: border-box;
    overflow-y: auto;
    padding: 10px 0 40px;
    @media (max-width: 1060px) {
      width: auto;
      border: none;
    }
    .addNewPage{
      margin: 10px 20px;
      display: block;
    }
    li{
      list-style: none;
      padding: 10px 20px;
      margin: 0 10px 4px;
      border-radius: 5px;
      cursor: pointer;
      transition: all .3s ease;
      box-sizing: border-box;
      // font-size: 17px;
      // font-weight: 600;
      position: relative;
      padding-right: 86px;
      &:hover{
        background-color: #f5f5fe;
      }
      .noName{
        color: #aaa;
        font-style: italic;
      }
      &.loading{
        background-color: #eee;
        height: 32px;
        border-radius: 50px;
        margin: 0 20px;
        cursor: default;
      }
      &.mainPage{
        position: relative;
        &:before{
          content: "";
          position: absolute;
          left: 2px; top: 12px;
          width: 14px; height: 14px;
          background-image: url(../assets/crown-orange.png);
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
        }
      }
      p{
        font-size: 13px;
        color: #767676;
        font-weight: normal;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .actionCnt{
        display: flex;
        align-items: center;
        position: absolute;
        right: 6px; top: 6px;
        .actionItem{
          margin-left: 3px;
          width: 30px; height: 30px;
          border-radius: 50%;
          background-size: 14px auto;
          background-position: center;
          background-repeat: no-repeat;
          cursor: pointer;
          position: relative;
          transition: box-shadow .3s ease, background-color .3s ease;
          &:hover{
            box-shadow: 2px 2px 4px rgba(0, 0, 0, .1);
            background-color: #fff;
          }
          &.more{
            background-image: url(../assets/more.png);
            opacity: .65;
            &.open{
              opacity: 1;
            }
          }
          .actionMenu{
            position: absolute;
            background-color: #fff;
            z-index: 7;
            padding: 8px 0;
            border-radius: 5px;
            box-shadow: 0 0 4px rgba(0, 0, 0, .2);
            right: 100%; top: 0;
            .divider{
              height: 1px;
              background-color: #eee;
              margin: 5px 0;
            }
            li{
              margin: 0;
              border-radius: 0;
              list-style: none;
              font-size: 15px;
              white-space: nowrap;
              padding: 8px 24px;
              padding-left: 16px;
              cursor: pointer;
              transition: all .3s ease;
              display: flex;
              align-items: center;
              &.red{
                color: red;
              }
              .icon{
                width: 18px; height: 18px;
                margin-right: 10px;
                background-size: 16px auto;
                background-position: center;
                background-repeat: no-repeat;
                &.link{
                  background-image: url(../assets/external-link-black.png);
                  background-size: 16px auto;
                  opacity: .65;
                }
                &.delete{
                  background-image: url(../assets/delete.png);
                }
                &.lock{
                  background-image: url(../assets/private-o.png);
                  background-size: 18px auto;
                }
                &.edit{
                  background-image: url(../assets/edit-black.png);
                  background-size: 15px auto;
                  opacity: .76;
                }
                &.main{
                  background-image: url(../assets/crown-black.png);
                  background-size: 15px auto;
                  opacity: .64;
                }
              }
              &:hover{
                background-color: #f5f5fe;
              }
            }
          }
        }
        .draft{
          color: red;
          font-size: 12px;
          border-radius: 3px;
          background-color: #fff4f4;
          padding: 2px 5px;
          text-transform: uppercase;
          border: red 1px solid;
        }
      }
      &.active{
        background-color: #bdd8ff;
        color: #222;
        p{
          color: #444;
        }
      }
    }
  }
  .ccTitle{
    position: absolute;
    right: 0; top: 0; left: 300px;
    border-bottom: #eee 1px solid;
    font-size: 17px;
    font-weight: 600;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding: 0 30px;
    height: 44px;
    margin: 0;
    .backBtn{
      width: 34px; height: 34px;
      border-radius: 50%;
      background-image: url(../assets/back-arrow.png);
      background-size: 16px auto;
      background-position: center;
      background-repeat: no-repeat;
      transition: all .3s ease;
      margin-right: 10px;
      cursor: pointer;
      margin-left: -7px;
      display: none;
      @media (max-width: 1060px) {
        display: block;
      }
      &:hover{
        background-color: #eee;
      }
    }
    @media (max-width: 1060px) {
      background-color: #fff;
      left: 0; width: auto;
    }
  }
  .endSection{
    border-top: #eee 1px solid;
    margin-top: 20px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    position: absolute;
    left: 300px; bottom: 0; right: 0;
    height: 60px;
    background-color: #fff;
    @media (max-width: 1060px) {
      left: 0;
    }
    .actionCnt{
      margin-left: auto;
      display: flex;
      align-items: center;
      .actionItem{
        width: 36px; height: 36px;
        border-radius: 50%;
        margin-left: 4px;
        transition: background-color .3s ease, opacity .3s ease;
        cursor: pointer;
        background-size: 20px auto;
        background-position: center;
        background-repeat: no-repeat;
        opacity: .46;
        position: relative;
        &.open{
          opacity: 1;
          .info{
            display: none !important;
          }
        }
        &.history{
          background-image: url(../assets/history.png);
        }
        &.draft{
          background-image: url(../assets/completed.png);
          background-size: 18px;
        }
        &.loading{
          background-image: url(../assets/loading-2.svg);
          background-size: 25px auto;
          opacity: .76;
        }
        .info{
          position: absolute;
          font-size: 12px;
          background-color: #222;
          padding: 4px 6px;
          display: none;
          bottom: 100%; left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          color: #fff;
          border-radius: 3px;
          &:before{
            content: "";
            position: absolute;
            left: 50%; top: 100%;
            border-top: #222 4px solid;
            border-left: transparent 4px solid;
            border-right: transparent 4px solid;
            margin-left: -2px;
          }
        }
        .actionMenu{
          background-color: #fff;
          position: absolute;
          bottom: 100%;
          padding: 8px 0;
          border-radius: 5px;
          left: 50%;
          transform: translateX(-50%);
          box-shadow: 0 0 4px rgba(0, 0, 0, .2);
          margin-bottom: 4px;
          cursor: default;
          &:before{
            content: "";
            position: absolute;
            left: 50%; top: 100%;
            border-top: #eee 6px solid;
            border-left: transparent 6px solid;
            border-right: transparent 6px solid;
            margin-left: -4px;
          }
          &:after{
            content: "";
            position: absolute;
            left: 50%; top: 100%;
            border-top: #fff 4px solid;
            border-left: transparent 4px solid;
            border-right: transparent 4px solid;
            margin-left: -2px;
            z-index: 3;
          }
          h4{
            white-space: nowrap;
            padding: 10px 20px;
            margin: 0;
            font-weight: 600;
            font-size: 16px;
          }
          .historyCnt{
            width: 340px;
            min-height: 240px;
            max-height: 340px;
            overflow: auto;
            border-top: #eee 1px solid;
            border-bottom: #eee 1px solid;
            padding: 6px 0;
            box-sizing: border-box;
            &.noData{
              padding: 16px 20px;
              color: #767676;
              font-size: 14px;
              text-align: center;
              display: flex;
              align-items: center;
              justify-content: center;
              .noDataIn{
                width: 100%;
              }
              img{
                display: block;
                width: 46px;
                margin: 0 auto 10px;
                opacity: .46;
              }
            }
            .historyItem{
              padding: 8px 20px;
              transition: all .3s ease;
              cursor: pointer;
              position: relative;
              padding-right: 76px;
              .historyStatus{
                position: absolute;
                right: 20px; top: 50%;
                transform: translateY(-50%);
                font-size: 14px;
                font-weight: 600;
                text-transform: uppercase;
                &.draft{
                  color: red;
                }
                &.published{
                  color: #0057d8;
                }
              }
              &:hover{
                background-color: #f5f5fe;
              }
              h5{
                margin: 0;
                font-size: 15px;
                font-weight: 600;
              }
              p{
                margin: 0;
                color: #767676;
                font-weight: normal;
                font-size: 14px;
                b{
                  font-weight: 600;
                  color: #444;
                }
              }

            }
            &.loading{
              background-image: url(../assets/loading-2.svg);
              background-size: 26px auto;
              background-position: center;
              background-repeat: no-repeat;
            }
          }
          .divider{
            height: 1px;
            background-color: #eee;
            margin: 5px 0;
          }
          li{
            list-style: none;
            font-size: 15px;
            white-space: nowrap;
            padding: 6px 24px;
            padding-left: 16px;
            cursor: pointer;
            transition: all .3s ease;
            display: flex;
            align-items: center;
            &.red{
              color: red;
            }
            .icon{
              width: 18px; height: 18px;
              margin-right: 10px;
              background-size: 18px auto;
              background-position: center;
              background-repeat: no-repeat;
              &.delete{
                background-image: url(../assets/delete.png);
              }
              &.save{
                background-image: url(../assets/save.png);
                background-size: 16px auto;
              }
            }
            &:hover{
              background-color: #f5f5fe;
            }
          }
        }
        &:hover{
          background-color: #eee;
          opacity: 1;
          .info{
            display: block;
          }
        }
      }
      .btn{
        margin-left: 6px;
        &.btnSecondary{
          font-weight: 600;
          font-size: 16px;
        }
      }
    }
  }
  .configContent{
    width: calc(100% - 300px);
    height: calc(100vh - 93px);
    box-sizing: border-box;
    overflow-y: auto;
    padding: 0 0 40px;
    margin-top: 44px;
    @media (max-width: 1060px) {
      position: absolute;
      width: auto;
      left: 0; right: 0; top: 0;
      background-color: #fff;
      z-index: 999;
    }
    &.hasEndSection{
      height: calc(100vh - 153px);
      padding-bottom: 20px;
    }
    &.loading{
      background-image: url(../assets/loading-2.svg);
      background-size: 46px auto;
      background-position: center;
      background-repeat: no-repeat;
    }
    h2{
      margin: 0;
      padding: 10px 40px;
      font-size: 22px;
      font-weight: 600;
    }
    .sectionItem{
      margin-top: 20px;
      padding: 0 0 16px;
      &.sortable-ghost{
        background-color: #eee !important;
        *{
          opacity: 0;
        }
      }
      &.sortable-chosen{
        background-color: #eee !important;
      }
      &.sortable-drag{
        background-color: #fff !important;
      }
      .sectionTitle{
        position: relative;
        border-bottom: #eee 1px solid;
        cursor: pointer;
        &:hover{
          // background-color: #f5f5f5;
        }
        .sectionOption{
          width: 34px; height: 34px;
          position: absolute;
          right: 30px; top: 8px;
          border-radius: 50%;
          background-size: 16px auto;
          background-position: center;
          background-repeat: no-repeat;
          opacity: .56;
          background-image: url(../assets/more.png);
          cursor: pointer;
          transition: background-color .3s ease;
          &.open{
            opacity: 1;
          }
          &:hover{
            background-color: #eee;
          }
          .sectionOptions{
            position: absolute;
            background-color: #fff;
            z-index: 7;
            padding: 8px 0;
            border-radius: 5px;
            box-shadow: 0 0 4px rgba(0, 0, 0, .2);
            right: 100%; top: 0;
            .divider{
              height: 1px;
              background-color: #eee;
              margin: 5px 0;
            }
            li{
              margin: 0;
              border-radius: 0;
              list-style: none;
              font-size: 15px;
              white-space: nowrap;
              padding: 8px 24px;
              padding-left: 16px;
              cursor: pointer;
              transition: all .3s ease;
              display: flex;
              align-items: center;
              &.red{
                color: red;
              }
              .icon{
                width: 18px; height: 18px;
                margin-right: 10px;
                background-size: 16px auto;
                background-position: center;
                background-repeat: no-repeat;
                &.link{
                  background-image: url(../assets/external-link-black.png);
                  background-size: 16px auto;
                  opacity: .65;
                }
                &.delete{
                  background-image: url(../assets/delete.png);
                }
                &.lock{
                  background-image: url(../assets/private-o.png);
                  background-size: 18px auto;
                }
                &.edit{
                  background-image: url(../assets/edit-black.png);
                  background-size: 15px auto;
                  opacity: .76;
                }
                &.main{
                  background-image: url(../assets/crown-black.png);
                  background-size: 15px auto;
                  opacity: .64;
                }
              }
              &:hover{
                background-color: #f5f5fe;
              }
            }
          }
        }
        .sectionDh{
          width: 34px; height: 34px;
          position: absolute;
          left: 6px; top: 8px;
          border-radius: 50%;
          background-size: 16px auto;
          background-position: center;
          background-repeat: no-repeat;
          opacity: .65;
          background-image: url(../assets/drag-and-drop.png);
          cursor: pointer;
          transition: all .3s ease;
        }
        .sectionDd{
          width: 34px; height: 34px;
          position: absolute;
          right: 68px; top: 8px;
          border-radius: 50%;
          background-size: 12px auto;
          background-position: center;
          background-repeat: no-repeat;
          opacity: .45;
          background-image: url(../assets/down-arrow.png);
          cursor: pointer;
          transition: all .3s ease;
          &:hover{
            background-color: #eee;
          }
        }
      }
      .sectionItemWrapper{
        transition: all .3s ease;
      }
      &.collapsed{
        padding-bottom: 0;
        .sectionTitle{
          .sectionDd{
            transform: rotate(180deg);
          }
        }
        .sectionItemWrapper{
          display: none;
        }
      }
    }
    .configItem{
      padding: 12px 40px;
      position: relative;
      &.configToggle{
        padding-bottom: 20px;
      }
      &.canEdit{
        cursor: pointer;
        transition: background-color .3s ease;
        &:before{
          content: "";
          position: absolute;
          left: 12px; top: 28px;
          width: 20px; height: 20px;
          background-image: url(../assets/edit-black.png);
          background-size: 12px auto;
          background-position: center;
          background-repeat: no-repeat;
          opacity: .56;
        }
        &:hover{
          background-color: #f5f5fe;
        }
      }
      .toggle{
        padding-top: 6px;
        .radioItem{
          padding-left: 60px;
        }
      }
      h4{
        margin: 0;
        font-weight: 600;
        text-transform: uppercase;
        font-size: 13px;
        color: #888;
      }
      p{
        margin: 0;
        white-space: pre-wrap;
        line-height: 1.3;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
                line-clamp: 3;
        -webkit-box-orient: vertical;
        &.hasColor{
          position: relative;
          padding-left: 26px;
          margin-top: 4px;
          span.color{
            position: absolute;
            width: 20px; height: 20px;
            border-radius: 3px;
            left: 0; top: 0;
            box-sizing: border-box;
            box-shadow: 2px 2px 4px rgba(0, 0, 0, .1);
          }
        }
        &:empty::before {
          content: 'Not set';
          color: #999;
          font-style: italic;
        }
      }
      .features{
        display: flex;
        padding: 6px 0 0;
        .featureItem{
          border-radius: 5px;
          border: #eee 1px solid;
          margin: 0 10px 0 0;
          padding: 10px 14px;
          box-sizing: border-box;
          width: 200px;
          h4{
            font-size: 17px;
            font-weight: normal;
            text-transform: none;
            color: #444;
            margin: 0 0 4px;
          }
          p{
            font-size: 14px;
            color: #767676;
          }
        }
      }
      .numberCnt{
        display: flex;
        flex-wrap: wrap;
        padding: 6px 0 0;
        margin-bottom: -10px;
        .numberItem{
          border-radius: 5px;
          border: #eee 1px solid;
          margin: 0 10px 10px 0;
          padding: 10px 14px 16px;
          // text-align: center;
          box-sizing: border-box;
          position: relative;
          cursor: pointer;
          min-height: 40px;
          h4{
            font-size: 24px;
            font-weight: 600;
            color: #444;
            text-transform: none;
            white-space: nowrap;
            margin: 0;
          }
          p{
            font-size: 13px;
            white-space: nowrap;
            margin: 0;
            color: #767676;
          }
          .delete{
            content: "";
            position: absolute;
            right: -8px; top: -8px;
            width: 20px; height: 20px;
            background-image: url(../assets/close-white.png);
            background-color: red;
            border-radius: 50%;
            background-size: 8px auto;
            background-position: center;
            background-repeat: no-repeat;
            cursor: pointer;
            transition: all .3s ease;
            opacity: .8;
            &:hover{
              opacity: 1;
              box-shadow: 2px 2px 4px rgba(0, 0, 0, .1);
            }
          }
          &:hover{
            background-color: #f5f5fe;
          }
          &.add{
            background-color: #eee;
            color: #999;
            font-weight: 600;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all .3s ease;
            line-height: 0;
            padding: 0 20px;
            .delete{
              display: none;
            }
            &:hover{
              background-color: #ddd;
              color: #767676;
            }
          }
          &.support{
            text-align: left;
            height: 68px;
            h4{
              font-size: 17px;
            }
            p{
              font-size: 15px;
            }
          }
        }
      }
      .galleryCnt{
        display: flex;
        padding: 6px 0 0;
        flex-wrap: wrap;
        margin-bottom: -10px;
        &.dataAuto{
          color: #767676;
          font-style: italic;
        }
        .galleryItem{
          margin: 0 10px 10px 0;
          width: 120px; height: 80px;
          background-color: #eee;
          border-radius: 5px;
          position: relative;
          background-size: cover;
          background-position: center;
          transition: all .3s ease;
          &:hover{
            box-shadow: 2px 2px 4px rgba(0, 0, 0, .1);
          }
          .galleryImg{
            position: absolute;
            left: 0; right: 0; top: 0; bottom: 0;
            border-radius: 5px;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            transition: opacity .3s ease;
            cursor: pointer;
            &.noImg{
              background-size: 36px auto;
              background-image: url(../assets/photo-black.png);
              opacity: .36;
              &:hover{
                opacity: .46;
              }
            }
          }
          .delete{
            content: "";
            position: absolute;
            right: -8px; top: -8px;
            width: 20px; height: 20px;
            background-image: url(../assets/close-white.png);
            background-color: red;
            border-radius: 50%;
            background-size: 8px auto;
            background-position: center;
            background-repeat: no-repeat;
            cursor: pointer;
            transition: all .3s ease;
            opacity: .8;
            &:hover{
              opacity: 1;
              box-shadow: 2px 2px 4px rgba(0, 0, 0, .1);
            }
          }
          &.add{
            background-color: #eee;
            color: #999;
            font-weight: 600;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all .3s ease;
            line-height: 0;
            padding: 0 20px;
            &:hover{
              background-color: #ddd;
              color: #767676;
            }
          }
          &.banner{
            height: 60px;
            cursor: pointer;
          }
          &.invLogo{
            height: 120px;
            cursor: pointer;
            background-size: contain;
            background-repeat: no-repeat;
            &.add{
              padding: 0;
            }
          }
          &.product{
            width: auto;
            height: auto;
            min-height: 54px;
            background-color: #fff;
            border: #eee 1px solid;
            padding: 6px 20px;
            padding-left: 60px;
            box-sizing: border-box;
            cursor: pointer;
            max-width: 260px;
            h4{
              text-transform: none;
              font-size: 16px;
              font-weight: 600;
              color: #444;
              margin: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            p{
              margin: -1px 0 0;
              font-size: 14px;
              color: #767676;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            &.add{
              background-color: #eee;
              padding: 0 20px;
            }
            .thumb{
              left: 8px;
              top: 6px;
              position: absolute;
              width: 40px;
              height: 40px;
              background-color: #e5e5e5;
              background-size: cover;
              background-position: center;
              background-repeat: no-repeat;
              border-radius: 4px;
              opacity: 1;
              display: inline-block;
              &.formField{
                background-color: transparent;
                background-size: 22px auto;
                background-position: center;
                opacity: .56;
                &.text{
                  background-image: url(../assets/short-text.png);
                }
                &.textarea{
                  background-image: url(../assets/long-text.png);
                }
                &.select{
                  background-image: url(../assets/dropdown.png);
                }
                &.checkbox{
                  background-image: url(../assets/checkbox.png);
                }
                &.date{
                  background-image: url(../assets/calendar.png);
                }
              }
              &.noImg{
                background-color: #ccc;
                opacity: .36;
                background-image: url(../assets/image-black.png);
                background-size: 20px auto;
                background-position: center;
                background-repeat: no-repeat;
              }
            }
          }
        }
      }
    }
  }
}
</style>
